import * as THREE from "three";
import Experience from "../Experience.js";
import { FontLoader } from "three/examples/jsm/loaders/FontLoader.js";
import { TextGeometry } from "three/examples/jsm/geometries/TextGeometry.js";
import { geomConfig } from "../config/geomConfig.js";
import debug from "debug";
import MakePartsPreprocessor from "./MakePartsPreprocessor.js";
const log = debug("app:3d");

/**
 * The `MakeGroup` class is responsible for creating and managing a Three.js group
 * @category Experience
 * @subcategory World
 */

export default class MakeGroup3D extends THREE.Group {
  /**
   * Constructs a MakeGroup instance.
   */

  constructor() {
    try {
      super();
      this.experience = new Experience();
      this.eventHub = this.experience.eventHub;
      this.scene = this.experience.scene;
      this.groupXMO = new THREE.Group();
    } catch (error) {
      console.error("Error in MakeGroup constructor:", error);
    }
  }

  /**
   * Layer depth values for different elements.
   */
  layerZ = { ring: 0.001, circle: 0.002, text: 0.001 };

  /**
   * Initializes the MakeGroup instance.
   *
   * @param {Object} params - Configuration parameters for the group.
   * @param {THREE.Object3D} shape - The shape to be added to the group.
   * @returns {THREE.Group} - The initialized groupXMO.
   */
  async init(params, shape) {
    try {
      log("this is 3D version");

      this.initializeGroup(params, shape);
      this.createPorts(params, shape);
      const bBox = this.createBoundingBox(params);
      this.setXMOPosition(params); // Added await here
      this.rotateXMO(params);

      log("rotated", params, "userData", this.groupXMO.userData);
      this.applyUserData(params);

      log("set position");

      return this.groupXMO;
    } catch (error) {
      console.error("Error in MakeGroup init:", error);
    }
  }

  /**
   * Initializes the group with its components.
   *
   * @param {Object} params - Configuration parameters.
   * @param {THREE.Object3D} shape - The shape to be added.
   * @param {Object} text - The text meshes.
   */
  initializeGroup(params, shape, text) {
    try {
      log("this is shape", shape);
      const { layer, w, h, glb, objectId, viewMode } = params;
      const textMesh = text?.textMesh;
      const mfText = text?.mfText;
      this.groupXMO.position.set(0, 0, 0); // Reset position
      this.groupXMO.rotation.set(0, 0, 0); // Reset rotation
      this.groupXMO.scale.set(1, 1, 1); // Reset scale to default (no scaling)
      this.groupXMO.name = "XMO";
      this.groupXMO.add(shape);
      if (textMesh && mfText) {
        this.groupXMO.add(textMesh, mfText);
      }

      if (layer === 50) {
        this.groupXMO.children.forEach((child) => {
          child.position.z += h / 2;
        });
      } else if (layer === 40) {
      } else if (layer === 33) {
        this.groupXMO.children.forEach((child) => {
          child.position.x += w / 2;
          child.position.z += h / 2;
        });
      } else {
        this.groupXMO.children.forEach((child) => {
          child.position.x += w / 2;
          child.position.z += h / 2;
        });
      }

      this.scene.add(this.groupXMO);
    } catch (error) {
      console.error("Error in MakeGroup initializeGroup:", error);
    }
  }

  createPorts(params, shape) {
    try {
      const { ports, w, h } = params;
      ports.forEach((port) => {
        const connPoint = new THREE.Group();
        const portX = port.x;
        const portY = -port.y;

        connPoint.position.set(portX, 0, -portY);
        // Align with MakeGroup.js so PortVault (e.g. canConnect for PROBE) behaves the same in 3D
        if (port.type === "INP") {
          connPoint.userData.typeOfPort = "INPUT";
        } else if (port.type === "OUT") {
          connPoint.userData.typeOfPort = "OUTPUT";
        } else if (port.type.startsWith("HY")) {
          connPoint.userData.typeOfPort = "HYBRID";
        } else if (port.type === "PRB") {
          connPoint.userData.typeOfPort = "PROBE";
        } else {
          connPoint.userData.typeOfPort = port.type;
        }
        connPoint.name = "ConnPoint";

        // Dummy circle to ensure PortVault.js does not break
        const dummyCircle = new THREE.Mesh(
          new THREE.CircleGeometry(0.08, 32),
          new THREE.MeshBasicMaterial({ visible: false })
        );
        dummyCircle.name = "circle";
        connPoint.add(dummyCircle);

        const direction = new THREE.Vector3();
        if (port.y === 0 && port.x !== 0) {
          direction.set(0, 0, -1);
        } else if (port.x === params.w && port.y !== 0) {
          direction.set(1, 0, 0);
        } else if (port.y === params.h && port.x !== 0) {
          direction.set(0, 0, 1);
        } else if (port.x === 0 && port.y !== 0) {
          direction.set(-1, 0, 0);
        }
        connPoint.direction = direction;
        this.groupXMO.add(connPoint);
      });
    } catch (error) {
      console.error("Error in MakeGroup3D createPorts:", error);
    }
  }

  /**
   * Creates a bounding box for the group.
   */
  /*   createBoundingBox() {
    try {
      const shape = this.groupXMO.getObjectByName("Shape");

      if (shape) {
        const box = new THREE.Box3().setFromObject(shape);
        const boxSize = new THREE.Vector3();
        box.getSize(boxSize);
        const geometry = new THREE.BoxGeometry(boxSize.x, boxSize.y, boxSize.z);

        const material = new THREE.MeshBasicMaterial({
          color: 0x00ff00,
          transparent: true,
          opacity: 0.75, // Fully transparent
        });

        const boundingBoxMesh = new THREE.Mesh(geometry, material);
        boundingBoxMesh.name = "bBox";

        const boxCenter = new THREE.Vector3();
        box.getCenter(boxCenter);
        boundingBoxMesh.position.copy(boxCenter);
        boundingBoxMesh.layers.set(20);
        this.groupXMO.add(boundingBoxMesh);
        return boxCenter;
      } else {
        console.warn("Shape not found in groupXMO");
      }
    } catch (error) {
      console.error("Error in MakeGroup createBoundingBox:", error);
    }
  } */

  createBoundingBox(params) {
    try {
      const shape = this.groupXMO.getObjectByName("Shape");
      if (!shape) {
        console.warn("Shape not found in groupXMO");
        return;
      }

      const box = new THREE.Box3().setFromObject(shape);
      const boxSize = new THREE.Vector3();
      box.getSize(boxSize);

      const geometry = new THREE.BoxGeometry(boxSize.x, boxSize.y, boxSize.z);
      const material = new THREE.MeshBasicMaterial({
        transparent: true,
        opacity: 0,
        wireframe: false,
      });

      const boundingBoxMesh = new THREE.Mesh(geometry, material);
      boundingBoxMesh.name = "bBox";

      const boxCenter = new THREE.Vector3();
      box.getCenter(boxCenter);
      boundingBoxMesh.position.copy(boxCenter);
      boundingBoxMesh.layers.set(20);

      this.groupXMO.add(boundingBoxMesh);
      return boxCenter;
    } catch (error) {
      console.error("Error in MakeGroup createBoundingBox:", error);
    }
  }

  /**
   * Rotates the XMO group based on the given parameters.
   *
   * @param {Object} params - Rotation parameters.
   */
  async rotateXMO(params) {
    try {
      log("params at rotate", params);
      const { version, angle, layer, partNumber } = params;
      log("version in rotate", version, typeof version);
      if (version === 1) {
        log("angle", angle);
        const makePartsPreprocessor = new MakePartsPreprocessor();
        const { x, z } = await makePartsPreprocessor.init({
          method: "rotate",
          arg: params,
        });
        log("x and z, angle", x, z, angle);
        this.groupXMO.position.x += x;
        this.groupXMO.position.z += z;
      }
      const axis = new THREE.Vector3(0, 1, 0); // Rotate around Y-axis
      const angleInRadians = THREE.MathUtils.degToRad(angle);
      this.groupXMO.rotateOnAxis(axis, angleInRadians);
    } catch (error) {
      console.error("Error in MakeGroup rotateXMO:", error);
    }
  }

  /**
   * Sets the position of the XMO group.
   *
   * @param {Object} params - Position parameters.
   */
  async setXMOPosition(params) {
    try {
      log("setting XMO position - params", params.layer);
      const { x, z, layer, viewMode } = params;
      log("thisisparams", params);
      const layerConfig = geomConfig.layers.find(
        (config) => config.layer === layer
      );
      log("set XMO position before", this.groupXMO.position);

      if (viewMode["3d"] === true) {
        const y = layerConfig.y * layerConfig.y3;
        log("this is y", y);
        this.groupXMO.position.set(x, y, z);
      } else {
        this.groupXMO.position.set(x, layerConfig.y2, z);
      }
      log("set XMO position after", this.groupXMO.position);
    } catch (error) {
      console.error("Caught an error in setXMOPosition:", error);
    }
  }

  applyUserData(params) {
    try {
      log("params userData", params);
      log("before userData", this.groupXMO.userData);
      this.groupXMO.userData = { ...params };
      this.groupXMO.userData.pn = params.partNumber;
      log("after userData", this.groupXMO.userData);
    } catch (error) {
      console.error("Error in MakeGroup applyUserData:", error);
    }

    this.eventHub.emit("loading-finished");
  }
}
